import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';

const initialFormState = {
  qt_ymd: '',
  language: 'ko',
  qt_audio_url: '',
  qt_audio_name: '',
};
const MAX_UPLOAD_FILES = 30;
const LANGUAGE_OPTIONS = ['ko', 'en', 'ja', 'es'];

const normalizeToYmd = (value) => (value ? value.replace(/-/g, '') : '');
const normalizeToYm = (value) => (value ? value.replace(/-/g, '').slice(0, 6) : '');
const formatForDateInput = (value) =>
  value && value.length === 8 ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}` : value || '';

/** YYYYMMDD / Date / ISO → YYYY-MM-DD */
const formatToYmdDisplay = (value) => {
  if (!value) return '-';
  const raw = String(value);
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return raw;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const getNextMonthStart = (ym) => {
  if (!ym || ym.length !== 6) return '';
  const year = Number(ym.slice(0, 4));
  const month = Number(ym.slice(4, 6));
  if (Number.isNaN(year) || Number.isNaN(month)) return '';
  const next = new Date(year, month, 1);
  return `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}01`;
};
const getCurrentMonthInput = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const getAudioFileName = (url) => {
  if (!url) return '-';
  try {
    const withoutQuery = String(url).split('?')[0].split('#')[0];
    const fileName = withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1);
    return decodeURIComponent(fileName || '-');
  } catch (error) {
    return '-';
  }
};

const parseQtYmdFromFileName = (fileName) => {
  if (!fileName) return '';
  const match = String(fileName).match(/qt_audio_(\d{8})/i);
  return match ? match[1] : '';
};

const getFileExtension = (fileName) => {
  if (!fileName) return '';
  const lastDot = String(fileName).lastIndexOf('.');
  if (lastDot < 0) return '';
  return String(fileName).slice(lastDot).toLowerCase();
};

/** 예: qt_ymd=20260701, file=큐티음성.wav → qt_audio_20260701.wav */
const buildQtAudioFileName = (qtYmd, originalFileName) => {
  const ymd = normalizeToYmd(qtYmd);
  if (!ymd || ymd.length !== 8) return '';
  const ext = getFileExtension(originalFileName) || '.mp3';
  return `qt_audio_${ymd}${ext}`;
};

const QtAudioInfoList = () => {
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterYm, setFilterYm] = useState(() => getCurrentMonthInput());
  const [filterLanguage, setFilterLanguage] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploadLanguage, setUploadLanguage] = useState('ko');
  const [formAudioFile, setFormAudioFile] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  useEffect(() => {
    fetchAudios(normalizeToYm(getCurrentMonthInput()));
  }, []);

  const normalizedFilterYm = useMemo(() => normalizeToYm(filterYm), [filterYm]);

  const fetchAudios = async (qtYm, language) => {
    setLoading(true);
    setErrorMessage('');
    try {
      let query = supabase
        .from('daily_scriptures')
        .select('id,qt_ymd,date,title,language,qt_audio_url,qt_audio_name,qt_audio_org_name,qt_audio_created_at,created_at')
        .not('qt_audio_url', 'is', null)
        .neq('qt_audio_url', '')
        .order('qt_ymd', { ascending: false })
        .order('qt_audio_created_at', { ascending: false });

      if (qtYm) {
        const startYmd = `${qtYm}01`;
        const nextMonthStart = getNextMonthStart(qtYm);
        query = query.gte('qt_ymd', startYmd);
        if (nextMonthStart) query = query.lt('qt_ymd', nextMonthStart);
      }
      if (language) query = query.eq('language', language);

      const { data, error } = await query;
      if (error) throw error;
      setAudios(data ?? []);
      setSelectedIds([]);
    } catch (error) {
      setErrorMessage(error.message || '데이터 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAudios(normalizedFilterYm, filterLanguage || undefined);
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedIds(audios.map((item) => item.id));
    else setSelectedIds([]);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: value,
        ...(name === 'qt_audio_url' ? { qt_audio_name: getAudioFileName(value) } : {}),
      };
      if (name === 'qt_ymd' && formAudioFile) {
        next.qt_audio_name = buildQtAudioFileName(value, formAudioFile.name);
      }
      return next;
    });
  };

  const handleFormAudioFileSelect = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const qtYmd = normalizeToYmd(formData.qt_ymd);
    if (!qtYmd || qtYmd.length !== 8) {
      setErrorMessage('음성 파일을 선택하기 전에 QT 일자를 먼저 입력해주세요.');
      return;
    }

    const renamed = buildQtAudioFileName(qtYmd, file.name);
    setFormAudioFile(file);
    setFormData((prev) => ({
      ...prev,
      qt_audio_name: renamed,
    }));
    setErrorMessage('');
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setEditingId(null);
    setFormAudioFile(null);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    setUploadSummary(null);

    if (files.length > MAX_UPLOAD_FILES) {
      setErrorMessage(`한 번에 최대 ${MAX_UPLOAD_FILES}개까지 업로드할 수 있습니다.`);
      setSelectedFiles([]);
      return;
    }

    const parsed = files.map((file) => {
      const qtYmd = parseQtYmdFromFileName(file.name);
      return { file, fileName: file.name, qt_ymd: qtYmd, valid: Boolean(qtYmd) };
    });

    const invalidCount = parsed.filter((item) => !item.valid).length;
    if (invalidCount > 0) {
      setErrorMessage(`파일명 규칙(qt_audio_YYYYMMDD)과 맞지 않는 파일이 ${invalidCount}개 있습니다.`);
    } else {
      setErrorMessage('');
    }

    setSelectedFiles(parsed);
  };

  const handleUploadSelectedFiles = async () => {
    if (!selectedFiles.length) {
      setErrorMessage('업로드할 음성 파일을 먼저 선택해주세요.');
      return;
    }
    if (selectedFiles.length > MAX_UPLOAD_FILES) {
      setErrorMessage(`한 번에 최대 ${MAX_UPLOAD_FILES}개까지 업로드할 수 있습니다.`);
      return;
    }
    if (!uploadLanguage) {
      setErrorMessage('업로드 언어를 선택해주세요.');
      return;
    }

    const invalidFiles = selectedFiles.filter((item) => !item.valid);
    if (invalidFiles.length > 0) {
      setErrorMessage('파일명 규칙(qt_audio_YYYYMMDD)을 확인해주세요.');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setUploadSummary(null);

    const results = { success: [], failed: [] };

    for (const item of selectedFiles) {
      const { file, qt_ymd, fileName } = item;
      try {
        const { data: scripture, error: scriptureError } = await supabase
          .from('daily_scriptures')
          .select('id')
          .eq('qt_ymd', qt_ymd)
          .eq('language', uploadLanguage)
          .maybeSingle();

        if (scriptureError) throw scriptureError;
        if (!scripture) {
          throw new Error(`해당 QT 일자(${qt_ymd}) / 언어(${uploadLanguage}) 본문이 없습니다.`);
        }

        const storagePath = `${uploadLanguage}/${qt_ymd}/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('qt_audio')
          .upload(storagePath, file, { upsert: true });
        if (uploadError) throw uploadError;

        const { data: publicData } = supabase.storage.from('qt_audio').getPublicUrl(storagePath);
        const publicUrl = publicData?.publicUrl || '';
        if (!publicUrl) throw new Error('업로드 URL 생성에 실패했습니다.');

        const { error: updateError } = await supabase
          .from('daily_scriptures')
          .update({
            qt_audio_url: publicUrl,
            qt_audio_name: fileName,
            qt_audio_org_name: file.name,
            qt_audio_created_at: new Date().toISOString(),
          })
          .eq('id', scripture.id);
        if (updateError) throw updateError;

        results.success.push({ fileName, qt_ymd, language: uploadLanguage });
      } catch (error) {
        results.failed.push({ fileName, reason: error.message || '알 수 없는 오류' });
      }
    }

    setUploadSummary(results);
    setUploading(false);
    setSelectedFiles([]);
    fetchAudios(normalizedFilterYm || undefined, filterLanguage || undefined);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedQtYmd = normalizeToYmd(formData.qt_ymd);
    const language = formData.language?.trim();

    if (!normalizedQtYmd || normalizedQtYmd.length !== 8) {
      setErrorMessage('QT 일자를 입력해주세요.');
      return;
    }
    if (!language) {
      setErrorMessage('언어를 선택해주세요.');
      return;
    }
    if (!formAudioFile && !editingId) {
      setErrorMessage('음성 파일을 선택해주세요.');
      return;
    }
    if (!formAudioFile && editingId && !formData.qt_audio_url?.trim()) {
      setErrorMessage('음성 파일을 선택하거나 기존 URL이 있어야 합니다.');
      return;
    }

    const resolvedAudioName = formAudioFile
      ? buildQtAudioFileName(normalizedQtYmd, formAudioFile.name)
      : formData.qt_audio_name?.trim() || getAudioFileName(formData.qt_audio_url);

    setSaving(true);
    setErrorMessage('');
    try {
      let scriptureId = editingId;
      if (!scriptureId) {
        const { data: scripture, error: scriptureError } = await supabase
          .from('daily_scriptures')
          .select('id')
          .eq('qt_ymd', normalizedQtYmd)
          .eq('language', language)
          .maybeSingle();

        if (scriptureError) throw scriptureError;
        if (!scripture) throw new Error('해당 QT 일자/언어의 본문이 없습니다. 큐티 본문을 먼저 등록해주세요.');
        scriptureId = scripture.id;
      }

      let publicUrl = formData.qt_audio_url?.trim() || '';
      if (formAudioFile) {
        const storagePath = `${language}/${normalizedQtYmd}/${resolvedAudioName}`;
        const { error: uploadError } = await supabase.storage
          .from('qt_audio')
          .upload(storagePath, formAudioFile, { upsert: true });
        if (uploadError) throw uploadError;

        const { data: publicData } = supabase.storage.from('qt_audio').getPublicUrl(storagePath);
        publicUrl = publicData?.publicUrl || '';
        if (!publicUrl) throw new Error('업로드 URL 생성에 실패했습니다.');
      }

      const updatePayload = {
        qt_ymd: normalizedQtYmd,
        language,
        qt_audio_url: publicUrl,
        qt_audio_name: resolvedAudioName,
        qt_audio_created_at: new Date().toISOString(),
      };
      if (formAudioFile) {
        updatePayload.qt_audio_org_name = formAudioFile.name;
      }
      const { error } = await supabase.from('daily_scriptures').update(updatePayload).eq('id', scriptureId);
      if (error) throw error;

      resetForm();
      fetchAudios(normalizedFilterYm || undefined, filterLanguage || undefined);
    } catch (error) {
      setErrorMessage(error.message || '음성 파일 저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (audio) => {
    setShowForm(true);
    setEditingId(audio.id);
    setFormAudioFile(null);
    setFormData({
      qt_ymd: formatForDateInput(audio.qt_ymd),
      language: audio.language || 'ko',
      qt_audio_url: audio.qt_audio_url || '',
      qt_audio_name: audio.qt_audio_name || getAudioFileName(audio.qt_audio_url),
    });
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm('선택한 음성 파일 매핑을 삭제하시겠습니까?')) return;

    setLoading(true);
    setErrorMessage('');
    try {
      const { error } = await supabase
        .from('daily_scriptures')
        .update({
          qt_audio_url: null,
          qt_audio_name: null,
          qt_audio_org_name: null,
          qt_audio_created_at: null,
        })
        .in('id', selectedIds);
      if (error) throw error;

      fetchAudios(normalizedFilterYm || undefined, filterLanguage || undefined);
    } catch (error) {
      setErrorMessage(error.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex align-items-center justify-content-between">
        <h6 className="m-0 font-weight-bold text-primary">음성 파일 관리</h6>
        <div>
          <button className="btn btn-primary btn-sm mr-2" onClick={handleSearch} disabled={loading}>
            조회
          </button>
          <button
            className="btn btn-info btn-sm mr-2"
            onClick={() => setShowBulkUpload((prev) => !prev)}
          >
            {showBulkUpload ? '일괄 업로드 숨김' : '일괄 업로드'}
          </button>
          <button
            className="btn btn-success btn-sm mr-2"
            onClick={() => {
              setShowForm((prev) => {
                if (prev) resetForm();
                return !prev;
              });
            }}
          >
            {showForm ? '입력 닫기' : '추가'}
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={!selectedIds.length || loading}>
            삭제
          </button>
        </div>
      </div>

      <div className="card-body">
        <div className="mb-3">
          <label className="font-weight-bold mr-2">검색년월</label>
          <input
            type="month"
            className="form-control w-auto d-inline-block mr-2"
            value={filterYm}
            onChange={(event) => setFilterYm(event.target.value)}
          />
          <label className="font-weight-bold mr-2">언어</label>
          <select
            className="form-control w-auto d-inline-block mr-2"
            value={filterLanguage}
            onChange={(event) => setFilterLanguage(event.target.value)}
          >
            <option value="">전체</option>
            {LANGUAGE_OPTIONS.map((language) => (
              <option key={language} value={language}>
                {language}
              </option>
            ))}
          </select>
        </div>

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        

        {showBulkUpload && (
        <div className="border rounded p-3 mb-4 bg-light">
          <h6 className="font-weight-bold mb-2">파일 일괄 업로드 (Supabase Storage: qt_audio)</h6>
          <p className="text-muted mb-2">
            파일명 규칙: <strong>qt_audio_YYYYMMDD</strong> (예: qt_audio_20260427.mp3), 최대 {MAX_UPLOAD_FILES}개
          </p>
          <div className="mb-2">
            <label className="font-weight-bold mr-2">업로드 언어</label>
            <select
              className="form-control w-auto d-inline-block"
              value={uploadLanguage}
              onChange={(event) => setUploadLanguage(event.target.value)}
              disabled={uploading}
            >
              {LANGUAGE_OPTIONS.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </div>
          <input
            type="file"
            className="form-control-file mb-2"
            accept="audio/*,.mp3,.m4a,.wav,.aac,.ogg"
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
          />
          {selectedFiles.length > 0 && (
            <div className="mb-2">
              <small className="text-muted d-block mb-1">선택 파일 {selectedFiles.length}개</small>
              <ul className="mb-0 pl-3">
                {selectedFiles.slice(0, 10).map((item) => (
                  <li key={item.fileName} className={item.valid ? 'text-success' : 'text-danger'}>
                    {item.fileName} {item.valid ? `-> ${item.qt_ymd}` : '-> 날짜 파싱 실패'}
                  </li>
                ))}
              </ul>
              {selectedFiles.length > 10 && (
                <small className="text-muted">...외 {selectedFiles.length - 10}개</small>
              )}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleUploadSelectedFiles}
            disabled={uploading || !selectedFiles.length}
          >
            {uploading ? '업로드 중...' : '선택 파일 업로드'}
          </button>
          {uploadSummary && (
            <div className="mt-2">
              <small className="d-block text-success">성공: {uploadSummary.success.length}건</small>
              <small className="d-block text-danger">실패: {uploadSummary.failed.length}건</small>
            </div>
          )}
        </div>
        )}

        <div
          className="table-responsive mb-3"
          style={{ maxHeight: 340, overflowY: 'auto' }}
        >
          <table className="table table-bordered">
            <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === audios.length && audios.length > 0}
                    onChange={(event) => handleSelectAll(event.target.checked)}
                  />
                </th>
                <th style={{ width: '80px' }}>QT 일자</th>
                <th style={{ width: '50px' }}>언어</th>
                <th style={{ width: '250px' }}>제목</th>
                <th style={{ width: '80px' }}>원본 파일명</th>
                <th style={{ width: '90px' }}>음성듣기</th>
                <th style={{ width: '150px' }}>수정일</th>
                <th style={{ width: '50px' }}>액션</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    로딩 중...
                  </td>
                </tr>
              ) : audios.length ? (
                audios.map((audio) => (
                  <tr key={audio.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(audio.id)}
                        onChange={() => handleCheckboxChange(audio.id)}
                      />
                    </td>
                    <td>{formatToYmdDisplay(audio.qt_ymd || audio.date)}</td>
                    <td>{audio.language || '-'}</td>
                    <td>{audio.title}</td>
                    <td>
                      <span
                        className="d-inline-block"
                        style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={audio.qt_audio_org_name || audio.qt_audio_name || getAudioFileName(audio.qt_audio_url)}
                      >
                        {audio.qt_audio_org_name || audio.qt_audio_name || getAudioFileName(audio.qt_audio_url) || '-'}
                      </span>
                    </td>
                    <td>
                      <a href={audio.qt_audio_url} target="_blank" rel="noreferrer">
                        듣기
                      </a>
                    </td>
                    <td>{formatToYmdDisplay(audio.qt_audio_created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => handleEditClick(audio)}
                      >
                        수정
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    조회된 데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showForm && (
          <div className="border rounded p-3 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="font-weight-bold mb-0">음성 파일 추가</h6>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ width: 70 }}
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                disabled={saving}
              >
                닫기
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group col-md-3">
                  <label>QT 일자</label>
                  <input
                    type="date"
                    name="qt_ymd"
                    className="form-control"
                    value={formData.qt_ymd}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="form-group col-md-2">
                  <label>언어</label>
                  <select
                    name="language"
                    className="form-control"
                    value={formData.language}
                    onChange={handleFormChange}
                    required
                  >
                    {LANGUAGE_OPTIONS.map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </div>
                {/* <div className="form-group col-md-7">
                  <label>음성 파일 URL</label>
                  <input
                    type="url"
                    name="qt_audio_url"
                    className="form-control"
                    value={formData.qt_audio_url}
                    onChange={handleFormChange}
                    required
                  />
                </div> */}
              </div>
              <div className="form-group">
                <label>음성 파일명</label>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <input
                    type="text"
                    name="qt_audio_name"
                    className="form-control"
                    value={formData.qt_audio_name}
                    onChange={handleFormChange}
                    placeholder="예: qt_audio_20260701.wav"
                    readOnly
                  />
                  <label className="btn btn-outline-secondary mb-0" style={{ whiteSpace: 'nowrap' }}>
                    파일 선택
                    <input
                      type="file"
                      accept="audio/*,.mp3,.m4a,.wav,.aac,.ogg"
                      style={{ display: 'none' }}
                      onChange={handleFormAudioFileSelect}
                      disabled={saving}
                    />
                  </label>
                </div>
                <small className="form-text text-muted">
                  저장 시 파일명은 <code>qt_audio_큐티일자(YYYYMMDD).확장자</code>로 저장됩니다.
                  {formAudioFile ? ` (원본: ${formAudioFile.name})` : ''}
                </small>
              </div>

              <button type="submit" className="btn btn-success mr-2" disabled={saving}
                style={{ 
                    display: showBulkUpload ? 'none' : 'block' ,
                    width: '100%' }}>
                {saving ? '저장 중...' : editingId ? '수정 완료' : '저장'}
              </button>
              {editingId && (
                <button type="button" className="btn btn-outline-secondary mt-2" onClick={resetForm} disabled={saving}>
                  새로 추가
                </button>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default QtAudioInfoList;
